/**
 * Base button atom with variant system (default/primary, secondary, destructive, ghost) and size options.
 * Uses class-variance-authority for type-safe variants. Supports asChild for Radix composition.
 */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex",
    "items-center",
    "justify-center",
    "gap-2",
    "whitespace-nowrap",
    "rounded-md",
    "text-sm",
    "font-medium",
    "ring-offset-white",
    "transition-colors",
    "focus-visible:outline-none",
    "focus-visible:ring-2",
    "focus-visible:ring-neutral-950",
    "focus-visible:ring-offset-2",
    "disabled:pointer-events-none",
    "disabled:opacity-50",
    "[&_svg]:pointer-events-none",
    "[&_svg]:size-4",
    "[&_svg]:shrink-0",
    "dark:ring-offset-neutral-950",
    "dark:focus-visible:ring-neutral-300",
  ],
  {
    variants: {
      variant: {
        default: [
          "bg-neutral-900",
          "text-neutral-50",
          "hover:bg-neutral-900/90",
          "dark:bg-neutral-50",
          "dark:text-neutral-900",
          "dark:hover:bg-neutral-50/90",
        ],
        primary: [
          "bg-neutral-900",
          "text-neutral-50",
          "hover:bg-neutral-900/90",
          "dark:bg-neutral-50",
          "dark:text-neutral-900",
          "dark:hover:bg-neutral-50/90",
        ],
        secondary: [
          "border",
          "border-neutral-200",
          "bg-white",
          "text-neutral-900",
          "hover:bg-neutral-100",
          "hover:text-neutral-900",
          "dark:bg-neutral-800",
          "dark:text-neutral-50",
          "dark:hover:bg-neutral-800/80",
        ],
        destructive: [
          "bg-red-500",
          "text-neutral-50",
          "hover:bg-red-500/90",
          "dark:bg-red-900",
          "dark:text-neutral-50",
          "dark:hover:bg-red-900/90",
        ],
        ghost: [
          "hover:bg-neutral-800",
          "hover:text-neutral-50",
          "dark:hover:bg-neutral-800",
          "dark:hover:text-neutral-50",
        ],
        brand: [
          "bg-lev-orange",
          "text-white",
          "hover:bg-lev-orange-dark",
          "dark:bg-lev-orange",
          "dark:hover:bg-lev-orange-dark",
        ],
      },
      size: {
        default: ["h-10", "px-4", "py-2"],
        sm: ["h-9", "rounded-md", "px-3"],
        lg: ["h-11", "rounded-md", "px-8"],
        icon: ["h-10", "w-10"],
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
